"use client";

import { useEffect, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import type { ITradingCompetitionPrizeBand } from "@/sanity/pages/PageTradingCompetitionTemplate";

export interface BoardRow {
  id: string;
  rank: number;
  name: string;
  /** The exact score. The public board never shows it below 0. */
  total: number;
  lastDay: number;
}

/** A looked-up trader, with the exact scores of the traders one place above and below. */
export interface RankHit extends BoardRow {
  ahead?: number;
  behind?: number;
}

/** What the live board needs from its data source. The dummy one lives in `dummyBoard.ts`. */
export interface BoardSource {
  /** The prize places, best first. */
  top: BoardRow[];
  /** Looks one trader up by the email of their account. Must run on the server once the board is real. */
  lookup: (email: string) => Promise<RankHit | null>;
}

/** Names stay private on the public board: "Kevin R." → "Kevin" (first name only). */
export const maskName = (name: string) => name.trim().split(/\s+/)[0];

/** The public board shows negative scores as 0. */
export const publicPoints = (row: BoardRow) => Math.max(0, row.total);

export const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export interface RankPrize {
  amount: number;
  payout?: "cash" | "voucher";
}

/** Each band covers the next `winners` places: 1, 2, 3, 4–10, 11–20… */
function bandRanges(bands: ITradingCompetitionPrizeBand[]) {
  let last = 0;
  return bands.map((b) => {
    const from = last + 1;
    last += b.winners ?? 0;
    return { from, to: last, prize: { amount: b.amount ?? 0, payout: stegaClean(b.payout) } as RankPrize };
  });
}

export function prizeForRank(bands: ITradingCompetitionPrizeBand[], rank: number): RankPrize | undefined {
  return bandRanges(bands).find((r) => rank >= r.from && rank <= r.to)?.prize;
}

/**
 * The result card's bar: progress to the next place up, i.e. how much of the gap between the trader
 * one place below and the trader one place above is already covered. Full for the leader.
 */
export function nextPlaceProgress(hit: RankHit) {
  if (hit.ahead === undefined) return 1;
  const floor = hit.behind ?? hit.total;
  if (hit.ahead <= floor) return hit.total >= hit.ahead ? 1 : 0;
  return Math.min(1, Math.max(0, (hit.total - floor) / (hit.ahead - floor)));
}

export const formatPrize = (amount: number) => `$${amount.toLocaleString("en-US")}`;

/**
 * `?test-leaderboard` (also typed as `?=test-leaderboard`) previews the live board with dummy data
 * and hides the launch countdown. Read after mount so the static HTML stays the pre-launch board.
 * When the real board goes live, switch on the launch date here and swap the dummy source.
 */
export function useLeaderboardPreview() {
  const [on, setOn] = useState(false);
  useEffect(() => setOn(window.location.search.includes("test-leaderboard")), []);
  return on;
}
